import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import ProceedCheckout from '@components/ProceedCheckout';
import HeartLaFourche from '@monofront/design-system/visuals/Icons/Old/HeartLaFourche';
import Button from '@monofront/design-system/components/Button';
import { useTranslations } from 'next-intl';
import { useCartMaxWeight } from '@hooks/useCartMaxWeight';
import { useEffect, useState } from 'react';
import { useSessionStorage } from '@monofront/business/hooks';
import { SESSIONSTORAGE_ENGAGED_FREETRIALER_CONFIRMATION } from '@constants/sessionStorage';
import Modal from '@monofront/design-system/organisms/Modal';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_WEB_FIRST_ORDER_FORM } from '@monofront/business/constants';

interface IUseModalFreetrialStarted {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

export const useModalFreetrialStarted = (): IUseModalFreetrialStarted => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [storedFreeTrialConfirmation, storeFreeTrialConfirmation] =
    useSessionStorage(SESSIONSTORAGE_ENGAGED_FREETRIALER_CONFIRMATION, '');
  const isFirstOrderFormABTestEnabled = useFeatureIsOn(
    FEATURE_FLAG_WEB_FIRST_ORDER_FORM,
  );

  useEffect(() => {
    const freeTrialConfirmation =
      storedFreeTrialConfirmation && JSON.parse(storedFreeTrialConfirmation);

    if (isFirstOrderFormABTestEnabled) {
      // desactivate the modal
      return;
    }

    if (freeTrialConfirmation && freeTrialConfirmation.showModal) {
      openModal();
      freeTrialConfirmation.showModal = false;
      storeFreeTrialConfirmation(JSON.stringify(freeTrialConfirmation));
    }
  }, [storedFreeTrialConfirmation]);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return {
    isModalOpen: isOpen,
    openModal,
    closeModal,
  };
};

interface IModalFreeTrialStartedProps {
  isOpen: boolean;
  closeModal: () => void;
  title?: string;
  subtitle?: string;
  buttonText?: string;
}

const ModalFreeTrialStarted: React.FC<IModalFreeTrialStartedProps> = ({
  isOpen,
  closeModal,
  title,
  subtitle,
  buttonText,
}) => {
  const { shoppingCart } = useShoppingCart();
  const tCart = useTranslations('Cart');
  const maxWeight = useCartMaxWeight();

  const isDisabledCheckoutCta =
    shoppingCart.totalPrice === 0 || shoppingCart.totalWeight >= maxWeight;

  return (
    <>
      <Modal
        isOpen={isOpen}
        externalToggle={closeModal}
        UNSAFE_className="modal-freetrial"
        hasPadding
      >
        <HeartLaFourche width={48} height={40} />
        <h2>{title || tCart('freeTrialStartedModalTitle')}</h2>
        <p>{subtitle || tCart('freeTrialStartedModalDesc')}</p>
        <Button
          variant={!isDisabledCheckoutCta ? 'secondary' : 'primary'}
          onClick={() => closeModal()}
          dataTestId="ModalFreeTrialStarted-Button-close"
        >
          {buttonText || tCart('freeTrialStartedModalClose')}
        </Button>
        {!isDisabledCheckoutCta && (
          <div className="cta-proceed-checkout">
            <ProceedCheckout
              paymentButtonLabel={tCart('continueToDelivery')}
              disabledButton={isDisabledCheckoutCta}
              dataTestId="ProceedCheckout-go-to-validate-and-pay"
            />
          </div>
        )}
      </Modal>
      <style jsx global>{`
        .modal-freetrial {
          top: 10%;
          max-width: 460px;
          text-align: center;
        }
        .modal-freetrial h2 {
          margin-top: var(--space-5);
          margin-bottom: var(--space-3);
          font-size: var(--font-size-2);
          font-family: var(--font-family-2);
        }
        .modal-freetrial p {
          margin-bottom: var(--space-6);
        }
        .modal-freetrial .cta-proceed-checkout {
          margin-top: var(--space-4);
        }
      `}</style>
    </>
  );
};

export default ModalFreeTrialStarted;
